import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Box,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArchiveIcon from "@mui/icons-material/Archive";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SyncIcon from "@mui/icons-material/Sync";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { useNavigate } from "react-router-dom";
import { apiClient } from "@/services/api";

import NewCreateProjectDialog from './NewCreateProjectDialog.tsx';
import { INDUSTRY_TYPES } from '@/constant/setting.ts';
import { showSuccessMessage, showErrorMessage } from '@/components/common/GlobalSnackbar.tsx';
import { useWebSocket } from '@/contexts/WebSocketContext.tsx';

// TypeScript 类型定义
interface User {
  id: number;
  username: string;
  display_name?: string;
  role: string;
  org_id?: number;
}

interface Project {
  id: number;
  project_name: string;
  project_code: string;
  description?: string;
  user_id: number;
  org_id?: number;
  report_template_id?: number;
  industry_type?: string;
  client_level?: string;
  evaluation_type?: string;
  status: string;
  contract_year?: number;
  dify_dataset_id?: string;
  project_type?: string;
  created_at: string;
  updated_at: string;
  report_generation_status?: string;
  report_generation_progress?: number;
  report_generation_error?: string;
  last_generated_at?: string;
  rag_sync_status?: string;
  rag_sync_progress?: number;
  rag_sync_error?: string;
  rag_sync_last_update?: string;
  user?: {
    id: number;
    username: string;
    display_name?: string;
  };
}

interface ProjectListItemProps {
  project: Project;
  onArchive: (project: Project, event: React.MouseEvent) => void;
  onDelete: (project: Project, event: React.MouseEvent) => void;
  onEdit: (projectId: number, event: React.MouseEvent) => void;
  onPreview: (projectId: number, event: React.MouseEvent) => void;
  onNavigate: (projectId: number, projectType?: string) => void;
  onGenerateReport: (project: Project, event: React.MouseEvent) => void;
  loadingStates: {
    editing: boolean;
    archiving: boolean;
    generating: boolean;
    projectId: number | null;
  };
  getUsernameById: (userId: number) => string;
  formatToGMT8: (dateString: string) => string;
  currentUser: User | null;
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
  [key: string]: any;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function ProjectListItem({
  project,
  onArchive,
  onDelete,
  onEdit,
  onPreview,
  onNavigate,
  onGenerateReport,
  loadingStates,
  getUsernameById,
  formatToGMT8,
  currentUser,
}: ProjectListItemProps) {
  // 检查项目是否正在自动处理中
  const isProcessing = project.report_generation_status === "generating";
  // 检查项目是否已完成报告生成
  const isCompleted = project.report_generation_status === "completed";

  return (
    <React.Fragment>
      <div
        onClick={() => {
          onNavigate(project.id, project.project_type);
        }}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "12px 0",
          cursor: "pointer",
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <span>{`${project.project_code}-${project.project_name}`}</span>
              {isProcessing && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "warning.main",
                    color: "warning.contrastText",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    boxShadow: 1
                  }}
                >
                  <CircularProgress size={14} sx={{ color: "warning.contrastText" }} />
                  <span>自动生成指标中</span>
                  <Box sx={{
                    backgroundColor: "rgba(255,255,255,0.3)",
                    px: 0.5,
                    borderRadius: 0.5,
                    fontSize: "0.75rem"
                  }}>
                    {project.report_generation_progress || 0}%
                  </Box>
                </Box>
              )}
              {isCompleted && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "success.main",
                    color: "success.contrastText",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    boxShadow: 1
                  }}
                >
                  <span>报告生成已完成</span>
                  {project.last_generated_at && (
                    <Box sx={{
                      backgroundColor: "rgba(255,255,255,0.3)",
                      px: 0.5,
                      borderRadius: 0.5,
                      fontSize: "0.75rem"
                    }}>
                      {formatToGMT8(project.last_generated_at)}
                    </Box>
                  )}
                </Box>
              )}

              {/* RAG同步状态显示 */}
              {project.rag_sync_status === 'syncing' && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "info.main",
                    color: "info.contrastText",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    boxShadow: 1
                  }}
                >
                  <CircularProgress size={14} sx={{ color: "info.contrastText" }} />
                  <span>同步知识库中</span>
                  <Box sx={{
                    backgroundColor: "rgba(255,255,255,0.3)",
                    px: 0.5,
                    borderRadius: 0.5,
                    fontSize: "0.75rem"
                  }}>
                    {project.rag_sync_progress || 0}%
                  </Box>
                </Box>
              )}
              {project.rag_sync_status === 'completed' && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "success.main",
                    color: "success.contrastText",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    boxShadow: 1
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                  <span>知识库已同步</span>
                  {project.rag_sync_last_update && (
                    <Box sx={{
                      backgroundColor: "rgba(255,255,255,0.3)",
                      px: 0.5,
                      borderRadius: 0.5,
                      fontSize: "0.75rem"
                    }}>
                      {formatToGMT8(project.rag_sync_last_update)}
                    </Box>
                  )}
                </Box>
              )}
              {project.rag_sync_status === 'failed' && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "error.main",
                    color: "error.contrastText",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    boxShadow: 1
                  }}
                >
                  <ErrorIcon sx={{ fontSize: 14 }} />
                  <span>同步失败</span>
                </Box>
              )}
            </Box>
          }
          secondary={
            <React.Fragment>
              <Box
                sx={{
                  display: "block",
                  mt: 0.5,
                  wordBreak: "break-all",
                  fontSize: "0.875rem",
                  color: "text.secondary",
                }}
              >
                年份: {project.contract_year || "未设置"} | 行业:{" "}
                {project.industry_type || "未设置"} | 客户级别:{" "}
                {project.client_level || "未设置"} | 评价类型:{" "}
                {project.evaluation_type || "未设置"} | 主评人:{" "}
                {getUsernameById(project.user_id)} | 日期:{" "}
                {formatToGMT8(project.created_at)}
              </Box>
            </React.Fragment>
          }
        />
        <Box
          sx={{ display: "flex", flexShrink: 0, pl: 2, alignItems: "center" }}
        >
          {project.status === "new" && (
            <>
              {/* 报告生成按钮和状态 */}
              {project.report_generation_status === "generating" ? (
                <Tooltip title="正在生成报告中">
                  <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 0.5 }} />
                    <Typography variant="caption" color="primary">
                      {project.report_generation_progress || 0}%
                    </Typography>
                  </Box>
                </Tooltip>
              ) : project.report_generation_status === "completed" ? (
                <>
                  <Tooltip title="报告生成已完成">
                    <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
                      <AutoAwesomeIcon sx={{ mr: 0.5, color: "success.main" }} />
                      <Typography variant="caption" color="success.main">
                        已完成
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title="重新生成指标">
                    <IconButton
                      onClick={(e) => onGenerateReport(project, e)}
                      disabled={loadingStates.generating}
                      color="warning"
                      size="small"
                    >
                      {loadingStates.generating &&
                        loadingStates.projectId === project.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <RefreshIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </>
              ) : null}

              {(currentUser?.role === "admin" ||
                currentUser?.role === "super_admin") && (
                  <Tooltip title="归档项目">
                    <IconButton
                      onClick={(e) => onArchive(project, e)}
                      disabled={loadingStates.archiving}
                    >
                      {loadingStates.archiving &&
                        loadingStates.projectId === project.id ? (
                        <CircularProgress size={24} />
                      ) : (
                        <ArchiveIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              <Tooltip title="编辑项目">
                <IconButton
                  onClick={(e) => onEdit(project.id, e)}
                  disabled={loadingStates.editing}
                >
                  {loadingStates.editing &&
                    loadingStates.projectId === project.id ? (
                    <CircularProgress size={24} />
                  ) : (
                    <EditIcon />
                  )}
                </IconButton>
              </Tooltip>
            </>
          )}
          {project.status === "archived_mentor" && (
            <Tooltip title="预览报告">
              <IconButton onClick={(e) => onPreview(project.id, e)}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}
          {project.status === "new" &&
            (currentUser?.role === "super_admin" ||
              currentUser?.role === "zhaoyuan_admin") && (
              <Tooltip title="删除项目">
                <IconButton onClick={(e) => onDelete(project, e)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          {project.status === "archived_mentor" &&
            (currentUser?.role === "super_admin" ||
              currentUser?.role === "zhaoyuan_admin") && (
              <Tooltip title="删除项目">
                <IconButton onClick={(e) => onDelete(project, e)}>
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
        </Box>
      </div>
      <Divider component="li" />
    </React.Fragment>
  );
}

export default function NewProjectList(): JSX.Element {
  const navigate = useNavigate();
  const { isConnected, subscribeProject, unsubscribeProject, onMessage } = useWebSocket();

  // Data states
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // User states
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // UI states
  const [tabValue, setTabValue] = useState<number>(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);

  // Action loading states
  const [loadingAction, setLoadingAction] = useState<{
    editing: boolean;
    archiving: boolean;
    generating: boolean;
    projectId: number | null;
  }>({ editing: false, archiving: false, generating: false, projectId: null });

  // 确认对话框状态
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [projectToArchive, setProjectToArchive] = useState<Project | null>(
    null,
  );
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  // WebSocket订阅的项目ID集合
  const [subscribedProjects, setSubscribedProjects] = useState<Set<number>>(new Set());

  // Filter states
  const [filterText, setFilterText] = useState<string>("");
  const [selectedIndustry, setSelectedIndustry] = useState<string>("all");
  const [selectedReviewer, setSelectedReviewer] = useState<string>("all");

  // 修改API调用以使用Python后端
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 获取所有项目（不按状态筛选），用于计算总数
      // 由于后端限制page_size最大为100，我们需要分页获取
      let allProjects: Project[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await apiClient.get('/projects/', {
          page: page,
          page_size: 100, // 使用后端允许的最大值
          sort_by: 'id',
          sort_order: 'desc'
        });

        if (response.success && response.data) {
          allProjects = allProjects.concat(response.data);
          // 检查是否还有更多数据
          hasMore = response.data.length === 100;
          page++;
        } else {
          throw new Error(response.message || '获取项目列表失败');
        }
      }

      // 确认获取到的项目数量
      const generatingCount = allProjects.filter(p => p.report_generation_status === 'generating').length;

      setProjects(allProjects);

      // 检查正在执行任务的项目状态
      await checkActiveReportGenerationTasks(allProjects);
    } catch (err: any) {
      setError("加载项目列表失败。");
    } finally {
      setLoading(false);
    }
  }, []); // 移除依赖，因为现在获取所有项目

  // 检查正在执行的报告生成任务
  const checkActiveReportGenerationTasks = useCallback(async (projectsList: Project[]) => {
    // 检查是否有项目正在生成报告
    const generatingProjects = projectsList.filter(project =>
      project.report_generation_status === "generating"
    );

    if (generatingProjects.length > 0) {

      // 发现正在生成报告的项目，正在获取最新状态
      for (const project of generatingProjects) {
        try {
          const response = await apiClient.get(`/projects/${project.id}/report-status`);

          if (response.success && response.data) {
            const statusData = response.data;


            // 更新项目状态 - 修复字段映射错误
            setProjects(prev => prev.map(p =>
              p.id === project.id
                ? {
                  ...p,
                  report_generation_status: statusData.report_generation_status || statusData.status,
                  report_generation_progress: statusData.report_generation_progress || statusData.progress || 0,
                  report_generation_error: statusData.report_generation_error || statusData.error,
                  last_generated_at: statusData.last_generated_at || p.last_generated_at
                }
                : p
            ));

          } else {
          }
        } catch (error) {
        }
      }
    } else {
    }
  }, []);

  // WebSocket消息处理
  useEffect(() => {
    if (!isConnected) return;

    const handleMessage = (message: any) => {

      // 处理项目更新和任务进度消息
      if (message.type === 'project_update' || message.type === 'report_progress' || message.type === 'task_progress') {
        // task_progress 消息的数据在根级别，其他消息的数据在 data 字段中
        const projectData = message.type === 'task_progress' ? message : message.data;

        if (projectData && projectData.project_id) {
          // 更新对应项目的状态 - 添加字段映射兼容性
          setProjects(prev => prev.map(project => {
            if (project.id === projectData.project_id) {
              return {
                ...project,
                report_generation_status: projectData.report_generation_status || projectData.status || project.report_generation_status,
                report_generation_progress: projectData.report_generation_progress || projectData.progress || project.report_generation_progress,
                report_generation_error: projectData.report_generation_error || projectData.error || project.report_generation_error,
                last_generated_at: projectData.last_generated_at || projectData.completed_at || project.last_generated_at
              };
            }
            return project;
          }));
        }
      }

      // 处理RAG同步消息
      if (['rag_update_started', 'rag_update_completed', 'rag_update_no_files', 'rag_update_failed'].includes(message.type)) {

        if (message.project_id) {
          const ragStatus = message.message || '';
          const isError = message.type === 'rag_update_failed';
          const isSuccess = message.type === 'rag_update_completed';
          const isNoFiles = message.type === 'rag_update_no_files';
          const isStarted = message.type === 'rag_update_started';

          setProjects(prev => prev.map(project => {
            if (project.id === message.project_id) {
              return {
                ...project,
                rag_sync_status: isError ? 'failed' : isSuccess ? 'completed' : isNoFiles ? 'no_files' : isStarted ? 'syncing' : 'idle',
                rag_sync_progress: isStarted ? 50 : (isSuccess || isNoFiles) ? 100 : 0,
                rag_sync_error: isError ? message.error_message : null,
                rag_sync_last_update: message.timestamp
              };
            }
            return project;
          }));
        }
      }
    };

    // 注册消息回调
    const unsubscribeMessage = onMessage(handleMessage);

    return () => {
      unsubscribeMessage();
    };
  }, [isConnected, onMessage]);

  // 管理项目订阅
  useEffect(() => {
    if (!isConnected) return;

    // 获取当前显示的项目ID
    const currentProjectIds = new Set(projects.map(p => p.id));
    const currentlySubscribed = subscribedProjects;

    // 订阅新项目
    currentProjectIds.forEach(projectId => {
      if (!currentlySubscribed.has(projectId)) {
        subscribeProject(projectId);
      }
    });

    // 取消订阅不再显示的项目
    currentlySubscribed.forEach(projectId => {
      if (!currentProjectIds.has(projectId)) {
        unsubscribeProject(projectId);
      }
    });

    // 更新订阅状态
    setSubscribedProjects(currentProjectIds);

  }, [projects, isConnected, subscribeProject, unsubscribeProject]);

  // 定期检查正在执行的任务状态（作为WebSocket的备份）
  // 注意：报告状态信息已不再展示，注释掉定时检查
  // useEffect(() => {
  //   if (!isConnected) {
  //     // 如果WebSocket未连接，定期检查任务状态
  //     const interval = setInterval(() => {
  //       const generatingProjects = projects.filter(project =>
  //         project.report_generation_status === "generating"
  //       );

  //       if (generatingProjects.length > 0) {
  //         // WebSocket未连接，定期检查任务状态
  //         checkActiveReportGenerationTasks(projects);
  //       }
  //     }, 10000); // 每10秒检查一次

  //     return () => clearInterval(interval);
  //   }
  // }, [isConnected, projects, checkActiveReportGenerationTasks]);

  useEffect(() => {
    const initializeData = async () => {
      // 先获取用户列表（用于主评人筛选，包含已删除用户）
      try {
        const filtersResponse = await apiClient.projects.getFilters();
        
        if (filtersResponse.success && filtersResponse.data && filtersResponse.data.users) {
          setUsers(filtersResponse.data.users);
        } else {
          // 如果获取失败，回退到使用users API（但会过滤已删除用户）
          const usersResponse = await apiClient.users.getList();
          if (usersResponse.success && usersResponse.data) {
            setUsers(usersResponse.data);
          } else if (Array.isArray(usersResponse)) {
            setUsers(usersResponse);
          }
        }
      } catch (error) {
        // 回退到使用users API
        try {
          const usersResponse = await apiClient.users.getList();
          if (usersResponse.success && usersResponse.data) {
            setUsers(usersResponse.data);
          } else if (Array.isArray(usersResponse)) {
            setUsers(usersResponse);
          }
        } catch (fallbackError) {
        }
      }

      // 然后获取项目列表
      await fetchProjects();

      try {
        const storedUser = localStorage.getItem("currentUser");
        if (storedUser) {
          const user = JSON.parse(storedUser);
          setCurrentUser(user);
        }
      } catch (e) {
      }
    };

    initializeData();
  }, []); // 移除fetchProjects依赖，避免无限循环

  // 当筛选条件改变时重新获取数据 - 已移除，避免重复请求

  // 非超级管理员时，强制停留在"正在编写"页签
  useEffect(() => {
    if (currentUser && currentUser.role !== "super_admin" && (tabValue === 1 || tabValue === 2)) {
      setTabValue(0);
    }
  }, [currentUser, tabValue]);

  const { inProgressProjects, archivedProjects, generatingProjects, totalInProgress, totalArchived, totalGenerating } = useMemo(() => {
    // 计算总数（所有项目）
    const totalInProgress = projects.filter(p => p.status === 'new').length;
    const totalArchived = projects.filter(p => p.status === 'archived_mentor').length;
    const totalGenerating = projects.filter(p => p.report_generation_status === 'generating').length;

    // 项目统计

    // 根据当前tab和筛选条件获取项目
    let filteredProjects = projects;

    // 按状态筛选
    if (tabValue === 0) {
      filteredProjects = projects.filter(p => p.status === 'new');
    } else if (tabValue === 1) {
      filteredProjects = projects.filter(p => p.status === 'archived_mentor');
    } else if (tabValue === 2) {
      filteredProjects = projects.filter(p => p.report_generation_status === 'generating');
    }

    // 按其他条件筛选
    if (filterText) {
      filteredProjects = filteredProjects.filter(p =>
        p.project_name.toLowerCase().includes(filterText.toLowerCase()) ||
        p.project_code.toLowerCase().includes(filterText.toLowerCase())
      );
    }

    if (selectedIndustry !== 'all') {
      filteredProjects = filteredProjects.filter(p => p.industry_type === selectedIndustry);
    }

    if (selectedReviewer !== 'all') {
      filteredProjects = filteredProjects.filter(p => p.user_id === parseInt(selectedReviewer));
    }

    const inProgress = filteredProjects.filter(p => p.status === 'new');
    const archived = filteredProjects.filter(p => p.status === 'archived_mentor');
    const generating = filteredProjects.filter(p => p.report_generation_status === 'generating');

    return {
      inProgressProjects: inProgress,
      archivedProjects: archived,
      generatingProjects: generating,
      totalInProgress,
      totalArchived,
      totalGenerating
    };
  }, [projects, tabValue, filterText, selectedIndustry, selectedReviewer]);

  const handleProjectCreatedOrUpdated = () => {
    fetchProjects();
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
  };

  const handleOpenCreateDialog = async () => {
    // 先检查用户是否有权限创建项目
    try {
      const response = await apiClient.get(
        `/auth/check-project-quota?userId=${currentUser?.id}&projectType=key_project`,
      );

      if (response.success && response.data) {
        // 权限检查通过，打开创建对话框
        setProjectToEdit(null);
        setEditMode(false);
        setIsCreateDialogOpen(true);
      } else {
        if (response.message && response.message.includes("配额")) {
          // 配额不足，直接弹出支付对话框
          window.dispatchEvent(
            new CustomEvent("showPaymentDialog", {
              detail: {
                reason: "quota_exceeded",
                message: "项目配额已用尽，请购买套餐继续使用",
              },
            }),
          );
        } else {
          // 无订阅，也弹出支付对话框
          window.dispatchEvent(
            new CustomEvent("showPaymentDialog", {
              detail: {
                reason: "no_subscription",
                message: "未开通套餐，请先购买套餐",
              },
            }),
          );
        }
      }
    } catch (error) {
      showErrorMessage("检查权限失败，请稍后重试");
    }
  };

  const handleOpenEditDialog = (projectId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setLoadingAction({ editing: true, archiving: false, projectId });
    const project = projects.find((p) => p.id === projectId);
    setProjectToEdit(project || null);
    setEditMode(true);
    setIsEditDialogOpen(true);
    setLoadingAction({ editing: false, archiving: false, projectId: null });
  };

  const handleArchiveClick = async (
    project: Project,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    setProjectToArchive(project);
    setArchiveConfirmOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (!projectToArchive) return;

    setLoadingAction({
      editing: false,
      archiving: true,
      projectId: projectToArchive.id,
    });
    try {
      // 使用Python后端的归档API
      const response = await apiClient.post(
        `/projects/${projectToArchive.id}/archive`,
      );

      setArchiveConfirmOpen(false); // 无论成功失败，先关闭对话框

      if (response.success) {
        showSuccessMessage("项目归档成功！");
        fetchProjects();
      } else {
        throw new Error(response.message || "归档失败，请稍后重试。");
      }
    } catch (err: any) {
      showErrorMessage(err.message);
    } finally {
      setLoadingAction({ editing: false, archiving: false, projectId: null });
      setProjectToArchive(null);
    }
  };

  const handleDeleteClick = async (
    project: Project,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    setProjectToDelete(project);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;

    try {
      // 使用Python后端的删除API
      const response = await apiClient.delete(
        `/projects/${projectToDelete.id}`,
      );

      if (response.success) {
        showSuccessMessage(response.message || "项目删除成功");
        fetchProjects();
        setDeleteConfirmOpen(false);
        setProjectToDelete(null);
      } else {
        throw new Error(response.message || "删除项目时发生错误");
      }
    } catch (err: any) {
      showErrorMessage(err.message || "删除失败");
    }
  };

  const handleNavigate = (projectId: number, projectType?: string) => {
    
    // 根据项目类型跳转到不同页面
    if (projectType === 'monitoring_target') {
      navigate(`/projects/${projectId}/monitoring-indicators`);
    } else if (projectType === 'performance_target') {
      navigate(`/projects/${projectId}/performance-indicators`);
    } else {
      // 默认跳转到探索界面
      navigate(`/projects/${projectId}`);
    }
  };
  const handlePreview = (projectId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(`/api/projects/${projectId}/writing/preview`, "_blank");
  };

  const handleGenerateReport = async (project: Project, event: React.MouseEvent) => {
    event.stopPropagation();
    setLoadingAction({ editing: false, archiving: false, generating: true, projectId: project.id });

    try {
      // 立即在前端更新项目状态为"generating"
      setProjects(prev => prev.map(p =>
        p.id === project.id
          ? {
            ...p,
            report_generation_status: "generating",
            report_generation_progress: 0,
            report_generation_error: null
          }
          : p
      ));

      const response = await apiClient.post(`/projects/${project.id}/generate-report`);

      if (response.success) {
        showSuccessMessage("报告生成任务已启动，正在后台处理");
        // 后端会通过WebSocket更新进度，这里不需要立即刷新
      } else {
        // 如果API调用失败，恢复原状态
        setProjects(prev => prev.map(p =>
          p.id === project.id
            ? {
              ...p,
              report_generation_status: "idle",
              report_generation_progress: 0,
              report_generation_error: response.message || "启动失败"
            }
            : p
        ));
        throw new Error(response.message || "启动报告生成失败");
      }
    } catch (err: any) {
      showErrorMessage(err.message || "启动报告生成失败，请稍后重试");
    } finally {
      setLoadingAction({ editing: false, archiving: false, generating: false, projectId: null });
    }
  };

  // 格式化日期（后端返回的时间已加8小时，但时区标记是+00:00，需要修正）
  const formatToGMT8 = useCallback((dateString: string): string => {
    if (!dateString) return "无";
    try {
      // 如果后端返回的时间时区标记是+00:00，但实际已经是北京时间，需要替换为+08:00
      let normalized = dateString.replace(" ", "T");
      // 将 +00:00 替换为 +08:00，因为后端返回的时间已经是北京时间
      if (normalized.includes("+00:00")) {
        normalized = normalized.replace("+00:00", "+08:00");
      }
      const date = new Date(normalized);
      if (isNaN(date.getTime())) return dateString;
      // 直接提取日期时间组件，不进行时区转换（因为时间已经是正确的北京时间）
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      return `${year}/${month}/${day} ${hour}:${minute}`;
    } catch {
      return dateString;
    }
  }, []);

  const getUsernameById = useCallback(
    (userId: number): string => {
      // getUsernameById called
      const user = users.find((u) => u.id === userId);
      // User found in users array
      return user ? user.display_name || user.username : "未指定";
    },
    [users],
  );

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={5}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;

  const commonListProps = {
    onArchive: handleArchiveClick,
    onDelete: handleDeleteClick,
    onEdit: handleOpenEditDialog,
    onPreview: handlePreview,
    onNavigate: handleNavigate,
    onGenerateReport: handleGenerateReport,
    loadingStates: loadingAction,
    getUsernameById,
    formatToGMT8,
    currentUser: currentUser,
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mt: 1, background: 'transparent' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} aria-label="project tabs">
          <Tab label={`正在编写 (${totalInProgress})`} id="project-tab-0" />
          {currentUser?.role === 'super_admin' && (
            <Tab label={`归档报告 (${totalArchived})`} id="project-tab-1" />
          )}
          {currentUser?.role === 'super_admin' && totalGenerating > 0 && (
            <Tab label={`正在生成报告 (${totalGenerating})`} id="project-tab-2" />
          )}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="搜索正在编写的项目..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>行业类型</InputLabel>
              <Select
                value={selectedIndustry}
                label="行业类型"
                onChange={(e) => setSelectedIndustry(e.target.value)}
              >
                <MenuItem value="all">全部类型</MenuItem>
                {INDUSTRY_TYPES.map((type: string) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* 正在编写状态下不显示主评人筛选 */}
            {tabValue !== 0 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>主评人</InputLabel>
                <Select
                  value={selectedReviewer}
                  label="主评人"
                  onChange={(e) => setSelectedReviewer(e.target.value)}
                >
                  <MenuItem value="all">全部主评人</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.display_name || user.username || "未知用户"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreateDialog}
          >
            创建新项目
          </Button>
        </Box>
        <List>
          {inProgressProjects.length > 0 ? (
            inProgressProjects.map((p) => (
              <ProjectListItem key={p.id} project={p} {...commonListProps} />
            ))
          ) : (
            <Alert severity="info">您当前没有正在编写的项目。</Alert>
          )}
        </List>
      </TabPanel>

      {currentUser?.role === "super_admin" && (
        <TabPanel value={tabValue} index={1}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="搜索归档报告..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>行业类型</InputLabel>
                <Select
                  value={selectedIndustry}
                  label="行业类型"
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                >
                  <MenuItem value="all">全部类型</MenuItem>
                  {INDUSTRY_TYPES.map((type: string) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>主评人</InputLabel>
                <Select
                  value={selectedReviewer}
                  label="主评人"
                  onChange={(e) => setSelectedReviewer(e.target.value)}
                >
                  <MenuItem value="all">全部主评人</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.display_name || user.username || "未知用户"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <List>
            {archivedProjects.length > 0 ? (
              archivedProjects.map((p) => (
                <ProjectListItem key={p.id} project={p} {...commonListProps} />
              ))
            ) : (
              <Alert severity="info">没有找到符合条件的归档报告。</Alert>
            )}
          </List>
        </TabPanel>
      )}

      {/* 正在生成报告的项目 Tab */}
      {totalGenerating > 0 && (
        <TabPanel value={tabValue} index={2}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Alert severity="info" sx={{ mb: 0 }}>
                正在生成报告的项目会显示在此处，系统会自动更新生成进度。
              </Alert>
            </Box>
          </Box>
          <List>
            {generatingProjects.length > 0 ? (
              generatingProjects.map((p) => (
                <ProjectListItem key={p.id} project={p} {...commonListProps} />
              ))
            ) : (
              <Alert severity="info">当前没有正在生成报告的项目。</Alert>
            )}
          </List>
        </TabPanel>
      )}

      {(isCreateDialogOpen || isEditDialogOpen) && (
        <NewCreateProjectDialog
          open={isCreateDialogOpen || isEditDialogOpen}
          onClose={() => {
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
          }}
          onProjectCreated={handleProjectCreatedOrUpdated}
          editMode={editMode}
          projectDataToEdit={projectToEdit}
        />
      )}

      {/* 归档项目确认对话框 */}
      <Dialog
        open={archiveConfirmOpen}
        onClose={() => setArchiveConfirmOpen(false)}
        aria-labelledby="archive-dialog-title"
        aria-describedby="archive-dialog-description"
      >
        <DialogTitle id="archive-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <ArchiveIcon color="info" />
            <Typography variant="h6">确认归档项目</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <div id="archive-dialog-description">
            <Typography variant="body1" gutterBottom>
              您确定要归档项目吗？此操作将处理数据并移至归档列表,
            </Typography>
            <Typography
              variant="body1"
              gutterBottom
              sx={{ color: "warning.main", fontWeight: "bold" }}
            >
              项目将不可修改或者恢复编辑状态。
            </Typography>
            {projectToArchive && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                项目名称：<strong>{projectToArchive.project_name}</strong>
              </Typography>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setArchiveConfirmOpen(false)}
            disabled={loadingAction.archiving}
          >
            取消
          </Button>
          <Button
            onClick={handleConfirmArchive}
            color="info"
            variant="contained"
            disabled={loadingAction.archiving}
            startIcon={
              loadingAction.archiving ? (
                <CircularProgress size={16} />
              ) : (
                <ArchiveIcon />
              )
            }
          >
            {loadingAction.archiving ? "归档中..." : "确认归档"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除项目确认对话框 */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">确认删除项目</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <div id="delete-dialog-description">
            <Typography variant="body1" gutterBottom>
              您确定要删除项目吗？此操作不可恢复。
            </Typography>
            {projectToDelete && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                项目名称：<strong>{projectToDelete.project_name}</strong>
              </Typography>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>取消</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            确认删除
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
